import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';

import { phoneAPI } from '../api/phoneAPI';
import { custonSooner } from '../../../components/CustopmSooner';
import { captureApiError } from '../../../lib/sentry-utils';

/**
 * Шаг 1: отправка заявки на смену номера. В phone уходит новый номер, а код
 * бэк шлёт на текущий — поэтому тост говорит про код, а не про новый номер.
 */
export const useSendPhoneCode = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (phone: string) => await phoneAPI.sendCode(phone),
    onSuccess: (res) => {
      if (res.data.success) {
        custonSooner.success(t('changePhone.code-sent'));
      } else {
        custonSooner.error(res.data.message ?? t('changePhone.error'));
      }
    },
    onError: (error) => {
      captureApiError(error, '/client/phone/send-code', 'POST');
      custonSooner.error(t('changePhone.error'));
    },
  });

  return {
    sendPhoneCode: mutateAsync,
    isPending,
  };
};

/**
 * Шаг 2: подтверждение текущего номера.
 *
 * Эндпоинт тот же, что и для нового номера, — бэк сам знает, какой номер
 * сверяет. Профиль здесь ещё не меняется: кэш не инвалидируем и «номер
 * изменён» не показываем, вместо этого бэк отправляет код на новый номер.
 */
export const useVerifyCurrentPhoneCode = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (code: string) => await phoneAPI.verifyCode(code),
    onSuccess: (res) => {
      if (!res.data.success) {
        custonSooner.error(res.data.message ?? t('changePhone.wrong-code'));
      }
    },
    onError: (error) => {
      captureApiError(error, '/client/phone/verify-code', 'POST');
      custonSooner.error(t('changePhone.error'));
    },
  });

  return {
    verifyCurrentPhoneCode: mutateAsync,
    isPending,
  };
};

/** Шаг 3: подтверждение нового номера — на нём номер в профиле и меняется. */
export const useVerifyPhoneCode = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (code: string) => await phoneAPI.verifyCode(code),
    onSuccess: (res) => {
      if (!res.data.success) {
        custonSooner.error(res.data.message ?? t('changePhone.wrong-code'));
        return;
      }

      // Номер лежит и в сторе, и в кэше профиля. Перезапрашиваем /user/me:
      // useGetUserProfile сам положит новые данные в стор, поэтому руками
      // править phone в redux не нужно — иначе разъедется с бэком.
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      custonSooner.success(t('changePhone.success'));
    },
    onError: (error) => {
      captureApiError(error, '/client/phone/verify-code', 'POST');
      custonSooner.error(t('changePhone.error'));
    },
  });

  return {
    verifyPhoneCode: mutateAsync,
    isPending,
  };
};
